import {Injectable} from '@angular/core';
import {Observable} from 'rxjs';
import {ConfigService} from 'app/core/services/config.service';
import {RequestService} from 'app/core/services/request.service';
import {of} from 'rxjs/observable/of';
import {ICancellationPolicy} from './models/cancellation-policy.interface';
import produce from "immer"

declare var moment: any;

@Injectable()
export class CourseEventService {

    getEnabledCourseCount() {
        let url = this.configService.get('server').url +
            this.configService.get('endpoints').solar.course.enabledCount.api;
        return this.requestService.get(url);
    }

    getVenueCountriesDict() {
        let url = this.configService.get('server').url +
            this.configService.get('endpoints').solar.address.countryDictionary.api + '?$country=1';
        return this.requestService.get(url);
    }

    getVenueCitiesDict(country: string) {
        let url = this.configService.get('server').url +
            this.configService.get('endpoints').solar.address.cityDictionary.api + '/' + country + '?$city=1';
        return this.requestService.get(url);
    }

    getVenuesInCity(city?: string, country?: string) {
        let url = this.configService.get('server').url +
            this.configService.get('endpoints').solar.address.listVenuesByCity.api;
        if (city) url += ('?@city=' + city)
        else if (country) url += ('?@country=' + country)
        else {
        }
        return this.requestService.get(url);
    }

    getCoursesDictByTypeListRoot(formatType: string): Observable<any> {
        let url = this.configService.get('server').url + this.configService.get('endpoints').solar.course.listRoot.api +
            '/' + formatType;
        return this.requestService.get(url);
    }

    getFilters(): any {
        return {
            title: 'Course Delivery',
            key: 'venue',
            show: true,
            state: '',
            options: [
                {checked: true, key: 'virtual', required: true, value: 'Virtual (VILT)', frequency: 0},
                {checked: true, key: 'classroom', required: true, value: 'Classroom (ILT)', frequency: 0},
                // { checked: true, key: 'Course Event', required: true, value: 'Course Event', frequency: 0 },
            ]
        }
    }

    getHistoricFilters(): any {
        return {
            title: 'Course Time Frame',
            key: 'historical',
            show: true,
            state: '',
            options: [
                {checked: true, key: 'past', required: true, value: 'Past Event', frequency: 0},
                {checked: true, key: 'future', required: true, value: 'Future Event', frequency: 0},
                // { checked: true, key: 'Course Event', required: true, value: 'Course Event', frequency: 0 },
            ]
        }
    }

    getPreCourseEventStatusDictionary(isUserAdmin: boolean): any[] {
        let result = [{key: 'released', value: 'Active'}, {key: 'finished', value: 'Finished'}]
        if (isUserAdmin) {
            result.push({key: 'draft', value: 'Draft'})
        }
        return result;
    }

    getCourseEventLearnerStatusDictionary(): any[] {
        return [{key: 'pending', value: 'Pending Approval'}, {key: 'active', value: 'Enrolled'}];
    }

    getAssessmentSlotStatusDictionary(): any[] {
        return [{key: 'active', value: 'Enrolled'}];
    }

    constructor(private configService: ConfigService, private requestService: RequestService) {
    }

    private courseEventDetailsRoute: string = '/course-management/course-events/{{key}}/details';
    private courseEventDetailsRouteSeller: string = '/product-seller/overview/course-management-seller/course-events/{{key}}/details';
    private courseEventCreateRoute: string = '/course-management/course-events/{{key}}/create';
    private courseEventCreateRouteSeller: string = '/product-seller/overview/course-management-seller/course-events/{{key}}/create';

    private selfPacedVariantDetailsRoute: string = '/course-management/self-paced-course-variants/{{key}}/details';
    private selfPacedVariantCreateRoute: string = '/course-management/self-paced-course-variants/{{key}}/create';

    // hideText()
    getCourseEventTypes() {
        return [
            {id: '1', configKey: 'virtual'},
            {id: '2', configKey: 'classroom'}
        ]
    }

    getCourseTypes(iltEnabled, selfPacedEnabled) {
        let res = [];
        if (selfPacedEnabled)
            res.push({id: '1', configKey: 'self-paced'})
        if (iltEnabled)
            res.push({id: '2', configKey: 'instructor-led'})

        return res
    }

    getCourseEventSelectTypes() {
        return [
            {id: '1', configKey: 'historical'},
            {id: '2', configKey: 'future'}
        ]
    }

    createCourseEvent(details: any): Observable<any> {
        const url = this.configService.get('server').url + this.configService.get('endpoints').solar.courseEvent.create.api;
        return this.requestService.post(url, details);
    }

    updateCourseEvent(details: any, step: string): Observable<any> {
        const url = this.configService.get('server').url + this.configService.get('endpoints').solar.courseEvent.update.api + '/' + step;
        return this.requestService.post(url, details);
    }

    getCourseEventById(id: string): Observable<any> {
        const url = this.configService.get('server').url + this.configService.get('endpoints').solar.courseEvent.getById.api + '/' + id;
        return this.requestService.get(url);
    }

    getConferencingToolDict(): Observable<any> {
        const url = this.configService.get('server').url + this.configService.get('endpoints').solar.courseEvent.getConferencingDict.api;
        return this.requestService.get(url);
    }

    getAllCourseEvents(): Observable<any> {
        const url = this.configService.get('server').url + this.configService.get('endpoints').solar.courseEvent.list.api;
        return this.requestService.get(url);
    }

    getCourseEventsForCourse(id: string): Observable<any> {
        const url = this.configService.get('server').url + this.configService.get('endpoints').solar.courseEvent.listByCourseId.api + '/' + id;
        return this.requestService.get(url);
    }

    getLxpLearners(): Observable<any> {
        const url = this.configService.get('server').url + '/api/lxp/users/list';
        return this.requestService.get(url);
    }

    getInstanceFields(): any[] {
        return [
            {
                title: 'Variant Name',
                name: 'name',
                config: {
                    type: 'link',
                    route: this.selfPacedVariantDetailsRoute,
                    key: 'id'
                },
                sortable: true
            },
            {
                title: 'Language',
                name: 'language',
                sortable: false
            },
            {
                title: 'Registered',
                name: 'enrolledLearnerCounter',
                sortable: true,
            },

        ];
    }

    getInstanceDraftFields(): any[] {
        return [
            {
                title: 'Variant Name',
                name: 'name',
                config: {
                    type: 'link',
                    route: this.selfPacedVariantCreateRoute,
                    key: 'id'
                },
                sortable: true
            },
            {
                title: 'Language',
                name: 'language',
                sortable: false
            },
            {
                title: 'Registered',
                name: 'enrolledLearnerCounter',
                sortable: true,
            },

        ];
    }

    getCourseEventDraftFields(isInternal: boolean): any[] {
        return [
            {
                title: 'Course Name',
                name: 'name',
                config: {
                    type: 'link',
                    route: isInternal ? this.courseEventCreateRoute : this.courseEventCreateRouteSeller,
                    key: 'id'
                },
                sortable: true
            },
            {
                title: 'Seats',
                name: 'maxSeats',
                sortable: false,
                formatter: (maxSeats, {enrolledLearnerCounter}) => maxSeats ? enrolledLearnerCounter + ' / ' + maxSeats : '0 / 0'
            },
            {
                title: 'Event Type',
                name: 'historical',
                formatter: (item) => item ? 'Past Event' : 'Future Event'
            }
        ];
    }

    getCourseEventFields(cancelEvent: boolean = false, isInternal: boolean): any[] {
        let res: any[] = [
            // {
            //   title: 'Code',
            //   name: 'code',
            //   config: {
            //     type: 'link',
            //     route: this.detailsRoute,
            //     key: 'id'
            // },
            //   sortable: true
            // },
            {
                title: 'Course Name',
                name: 'name',
                config: {
                    type: 'link',
                    route: isInternal ? this.courseEventDetailsRoute : this.courseEventDetailsRouteSeller,
                    key: 'id'
                },
                sortable: true,
                weight: 3
            },
            {
                title: 'Event Venue',
                name: 'venue',
                sortable: true,
                weight: 3,
                formatter: (venue, item) => this.formatAddress(venue, item)
            },
            {
                title: 'Start Date',
                name: 'startDate',
                sortable: false,
                weight: 2,
                formatter: (date) => this.formatDate(date)
            },
            {
                title: 'End Date',
                name: 'endDate',
                sortable: false,
                weight: 2,
                formatter: (date) => this.formatDate(date)
            },

            {
                title: 'Seats',
                name: 'maxSeats',
                sortable: false,
                weight: 1,
                formatter: (maxSeats, {enrolledLearnerCounter}) => maxSeats ? enrolledLearnerCounter + ' / ' + maxSeats : '0 / 0'
            }];
        if (cancelEvent) {
            res.push(
                {
                    title: '',
                    config: {
                        type: 'template',
                        template: 'cancelEvent'
                    },
                    weight: 3
                })
        }
        return res;
    }

    getLearnersEnrolledFields(classEnded: boolean, addCompletion?: boolean, userLock?: boolean): any[] {
        let response = [];
        response.push(
            {
                title: 'Email',
                name: 'username'
            }, {
                title: 'Enrollment Date',
                name: 'enrolmentDate',
                formatter: (date) => this.formatDate(date)
            });
        if (addCompletion) {
            response.push({
                title: 'Completed',
                name: 'completedEvent',
                config: {
                    type: 'template',
                    template: 'completedEvent'
                }
            })
        }
        if (!classEnded) {
            response.push({
                title: '',
                config: {
                    type: 'template',
                    template: 'removeLearner'
                }
            });
        }
        return response;
    }

    getHistoricalLearnersEnrolledFields(): any[] {
        return [
            {
                title: 'Email',
                name: 'username'
            }, {
                title: 'Enrollment Date',
                name: 'enrolmentDate',
                formatter: (date) => this.formatDate(date)
            }, {
                title: 'Completion Date',
                name: 'markedCompletedOn',
                formatter: (date) => this.formatDate(date)
            }
        ];
    }

    getLearnersPendingFields(userLock: boolean): any[] {
        return [
            {
                title: 'Email',
                name: 'username'
            },
            {
                title: 'Enrollment Date',
                name: 'enrolmentDate',
                formatter: (date) => this.formatDate(date)
            }, {
                title: '',
                config: {
                    type: 'action',
                    actions: [
                        {
                            name: 'reject',
                            label: 'Reject',
                            class: 'learner-reject',
                            icon: 'fal fa-times-circle',
                            disabled: _ => userLock
                        },
                        {
                            name: 'approve',
                            label: 'Approve',
                            class: 'learner-approve',
                            icon: 'fal fa-check',
                            disabled: _ => userLock
                        }
                    ]
                }
            }
        ];
    }

    private formatDate(date: any) {
        return date ? moment(new Date(date)).format('D MMM YYYY') : 'N/A';
    }

    private formatAddress(venue: any, item: any) {
        return item.virtualVenue ? 'Virtual' : item.venue ?
            [
                // data.venue.address, data.venue.houseNumber, data.venue.postalCode,
                venue.city, venue.country].filter(v => v).join(', ') : 'N/A'
    }

    getTimezoneDict(): Observable<any> {
        const url = this.configService.get('server').url + this.configService.get('endpoints').solar.timezone.dictionary.api;
        return this.requestService.get(url);
    }

    updateAttribute(data: any, attribute: string): Observable<any> {
        // If the event has 900+ learners the request returns 413
        if (data['classEvent'] && data['classEvent']['learners']) {
            //data['classEvent']['learners'] = null;
            data = produce(data, (draft: any) => {
                draft.classEvent.learners = null
            });
        }
        const url = this.configService.get('server').url + this.configService.get('endpoints').solar.courseEvent.editAttribute.api + '/' + attribute;
        return this.requestService.post(url, data);
    }

    getAddressList(): Observable<any> {
        const url = this.configService.get('server').url + this.configService.get('endpoints').solar.address.list.api;
        return this.requestService.get(url);
    }

    getCountryList(): Observable<any> {
        // const url = this.configService.get('server').url + this.configService.get('endpoints').solar.address.list.api;
        // return this.requestService.get(url);
        return of([
            {key: 'tr', value: 'Turkey'},
            {key: 'nl', value: 'Netherlands'},
            {key: 'ita', value: 'Italy'},
            {key: 'fr', value: 'France'},
            {key: 'es', value: 'Spain'},
            {key: 'en', value: 'England'},
            {key: 'ir', value: 'Ireland'},
            {key: 'gr', value: 'Greece'},
            {key: 'bl', value: 'Belgium'},
            {key: 'uk', value: 'Ukraine'},
            {key: 'sw', value: 'Switzerland'},
            {key: 'swe', value: 'Sweden'},
        ])
    }

    getCountryDict(): Observable<any> {
        let url = this.configService.get('server').url + this.configService.get('endpoints').agora.dictionary.country.api;
        return this.requestService.get(url, {verbose: true, type: 'api'});
    }

    getStatesByCountryDict(country: string): Observable<any> {
        let url = this.configService.get('server').sahara + this.configService.get('endpoints').sahara.dictionary.statesByCountry.api + '/' + country;
        return this.requestService.get(url, {verbose: true, type: 'api'});
    }

    addAddress(data: any): Observable<any> {
        let url = this.configService.get('server').url + this.configService.get('endpoints').solar.address.add.api;
        return this.requestService.post(url, data);
    }

    addInstructor(data: any): Observable<any> {
        // const payload = {...data, type: 'instructor'};
        let url = this.configService.get('server').url + this.configService.get('endpoints').solar.courseEvent.instructor.createOrUpdate.api;
        return this.requestService.post(url, data);
    }

    assignLearners(classEventId: string, emails: string[]): Observable<any> {
        let url = this.configService.get('server').url +
            this.configService.get('endpoints').solar.courseEvent.learner.add.api + '/' + classEventId;
        return this.requestService.post(url, emails);
    }

    removeLearners(classEventId: string, emails: string[]): Observable<any> {
        let url = this.configService.get('server').url +
            this.configService.get('endpoints').solar.courseEvent.learner.remove.api + '/' + classEventId;
        return this.requestService.post(url, emails);
    }

    approveLearner(data: any): Observable<any> {
        let url = this.configService.get('server').url +
            this.configService.get('endpoints').solar.courseEvent.learner.approval.approve.api;
        return this.requestService.post(url, data);
    }

    rejectLearner(data: any): Observable<any> {
        let url = this.configService.get('server').url +
            this.configService.get('endpoints').solar.courseEvent.learner.approval.reject.api;
        return this.requestService.post(url, data);
    }

    getTrainingManagerDict(): Observable<any> {
        let url = this.configService.get('server').url +
            this.configService.get('endpoints').agora.employees.trainingManagerDictionary.api;
        return this.requestService.get(url);
    }

    getTrainingManagerList(): Observable<any> {
        let url = this.configService.get('server').url +
            this.configService.get('endpoints').agora.employees.trainingManagerList.api + '?showEmployeeId=true';
        return this.requestService.get(url);
    }

    getCourseEventLanguages(showKeys: boolean): Observable<any> {
        let url = this.configService.get('server').url + this.configService.get('endpoints').solar.courseEvent.deliveryLanguagesDict.api + '?sortByValue=true';
        if (showKeys) url += '&showKeys=true';
        return this.requestService.get(url);
    }

    isInstructorBusy(instructorId: string, dates: any[], eventId: string): Observable<any> {
        const url = this.configService.get('server').url + this.configService.get('endpoints').solar.courseEvent.listByInstructorOnDate.api + '/' + instructorId;
        return this.requestService.get(url, {date: dates, eventId: eventId});
    }

    isInstructorsBusy(instructors: any[], dates: any[], eventId: string): Observable<any> {
        let data = {dates: dates, eventId: eventId, instructors: instructors};
        const url = this.configService.get('server').url + this.configService.get('endpoints').solar.courseEvent.listByInstructorsOnDate.api;
        return this.requestService.post(url, data);
    }

    learnersBulkImport(data: any, eventId: string): Observable<any> {
        let url = this.configService.get('server').url +
            this.configService.get('endpoints').solar.courseEvent.learner.bulkImport.api + '/' + eventId;
        return this.requestService.post(url, data);
    }

    cancelEvent(data: any) {
        let url = this.configService.get('server').url +
            this.configService.get('endpoints').solar.courseEvent.cancelEvent.api;
        return this.requestService.post(url, data);
    }

    getCourseMaterialsDetails(skus: string[]) {
        let url = this.configService.get('server').url +
            this.configService.get('endpoints').library.internalRepository.fullDetailsBySKU.api + '?sku=' + skus.join(',');
        return this.requestService.get(url);
    }

    getTimezonesDictionary(): Observable<any> {
        let url = this.configService.get('server').url + this.configService.get('endpoints').solar.timezone.dictionary.api;
        return this.requestService.get(url);
    }

    getExamsListInCourseEvent(courseEventId: string, sku: string): Observable<any> {
        let url = this.configService.get('server').url + this.configService.get('endpoints').solar.courseEvent.exams.list.api + '/' + courseEventId + '?examVariantSKU=' + sku;
        return this.requestService.get(url);
    }

    getMaterialTracking(courseEventId: string, sku: string): Observable<any> {
        let url = this.configService.get('server').url + this.configService.get('endpoints').solar.courseEvent.materialTrack.api + '?courseEventId=' + courseEventId + '&materialSKU=' + sku;
        return this.requestService.get(url);
    }

    getAttendanceByAgenda(courseEventId: string, params?: any): Observable<any> {
        let url = this.configService.get('server').url + this.configService.get('endpoints').solar.courseEvent.attendanceByAgenda.api + '/' + courseEventId;
        return this.requestService.get(url, params);
    }

    setLearnerAttendance(courseEventId: string, data: any) {
        let url = this.configService.get('server').url + this.configService.get('endpoints').solar.courseEvent.attendanceChangeForLearner.api + '/' + courseEventId;
        return this.requestService.post(url, data);
    }

    validateLearnerCSV(courseEventId: string, data: any) {
        let url = this.configService.get('server').url + this.configService.get('endpoints').solar.courseEvent.learner.csvUpload.validation.api + '/' + courseEventId;
        return this.requestService.post(url, data);
    }

    validateLearnerSession(courseEventId: string, params?: any) {
        let url = this.configService.get('server').url + this.configService.get('endpoints').solar.courseEvent.validateSessionAccess.api + '/' + courseEventId;
        return this.requestService.get(url, params);
    }

    uploadLearnerCSV(courseEventId: string, data: any) {
        let url = this.configService.get('server').url + this.configService.get('endpoints').solar.courseEvent.learner.csvUpload.upload.api + '/' + courseEventId;
        return this.requestService.post(url, data);
    }

    getWaitingList(classEventId: string, params?: any) {
        let url = this.configService.get('server').url +
            this.configService.get('endpoints').solar.courseEvent.learner.list.api + '?id=' + classEventId + '&status=waiting-list';
        return this.requestService.get(url, params);
    }

    disableWaitingList(eventId) {
        let url = this.configService.get('server').url +
            this.configService.get('endpoints').solar.courseEvent.waitingList.disable.api + '/' + eventId;
        return this.requestService.post(url, {});
    }

    enableWaitingList(eventId: string, data) {
        let url = this.configService.get('server').url +
            this.configService.get('endpoints').solar.courseEvent.waitingList.enable.api + '/' + eventId;
        return this.requestService.post(url, data);
    }

    promoteLearner(classEventId: string, data) {
        let url = this.configService.get('server').url +
            this.configService.get('endpoints').solar.courseEvent.waitingList.promoteLearner.api + '/' + classEventId;
        return this.requestService.post(url, data);
    }

    demoteLearner(classEventId: string, data) {
        let url = this.configService.get('server').url +
            this.configService.get('endpoints').solar.courseEvent.waitingList.demoteLearner.api + '/' + classEventId;
        return this.requestService.post(url, data);
    }

    removeLearnerFromWaitingList(classEventId: string, data) {
        let url = this.configService.get('server').url +
            this.configService.get('endpoints').solar.courseEvent.waitingList.removeLearner.api + '/' + classEventId;
        return this.requestService.post(url, data);
    }

    markBulkAttendance(courseEventId: string, params?: any): Observable<any> {
        let url = this.configService.get('server').url + this.configService.get('endpoints').solar.courseEvent.bulkAttendance.api + '/' + courseEventId;
        return this.requestService.post(url, params);
    }

    validateBulkCompletion(courseEventId: string, params?: any, allUsers?: boolean): Observable<any> {
        let url = this.configService.get('server').url + this.configService.get('endpoints').solar.courseEvent.validateCompletion.api + '/' + courseEventId + '?allUsers=' + allUsers;
        return this.requestService.post(url, params.users);
    }

    markBulkCompletion(courseEventId: string, data?: any): Observable<any> {
        let url = this.configService.get('server').url +
            this.configService.get('endpoints').solar.courseEvent.attendance.api + '/' + courseEventId;
        return this.requestService.post(url, data);
    }

    unmarkCompletion(courseEventId: string, params?: any): Observable<any> {
        let url = this.configService.get('server').url + this.configService.get('endpoints').solar.courseEvent.unmarkAttendance.api + '/' + courseEventId;
        return this.requestService.post(url, params);
    }

    getCancellationPolicies(): Observable<{ data: ICancellationPolicy[] }> {
        let url = this.configService.get('server').url + this.configService.get('endpoints').solar.courseEvent.seatCancellationPolicies.api;
        return this.requestService.get(url);
    }

    getILTEventDetails(id: string): Observable<any> {
        let url = this.configService.get('server').url + this.configService.get('endpoints').solar.courseEvent.seatCancellationPolicies.api;
        return this.requestService.get(url);
    }

    setCurrency(currency) {
        let currencySign;
        switch (currency) {
            case 'EUR':
                currencySign = '€';
                break;
            case 'USD':
                currencySign = '$';
                break;
            case 'DKK':
                currencySign = 'Kr.';
                break;
            case 'INR':
                currencySign = '₹';
                break;
            default:
                currencySign = ''
        }
        ;
        return currencySign;
    }

    removeSelectedInstructorFromList(instructorsList$, instructorsControl): Observable<any[]> {
        return new Observable((observer) => {
            instructorsList$.subscribe((instructors) => {
                let filterInstructor = instructors
                    .map(items => {
                        if (instructorsControl.value.filter(item => item.id == items.id).length == 0) {
                            items.isVisible = true;
                            return items;
                        } else {
                            items.isVisible = false;
                            return items;
                        }
                    })
                    .filter(item => item);
                observer.next(filterInstructor);
            });
        });
    }
}



// WEBPACK FOOTER //
// ./src/app/common/courses/course-event.service.ts